import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { client, MarketSupplier, Instance, Market } from '../../../app/services/api/orderManagementClient';
import useLogError from '../../../hooks/useLogError';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';
import { useDemoScenario } from '../../../contexts/DemoScenarioContext';
import { isDemoModeEnabled } from '../../../utils/featureAccess';
import { getSupplierGovernanceProfile, createQuickOnboardGovernanceProfile } from '../../../mocks/data/supplierGovernanceData';
import GovernanceTab from './components/GovernanceTab';
import { Alert as MuiAlert } from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel"
      hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const SupplierForm = () => {
  const { t } = useTranslation('suppliers');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { instances, getMarkets, ensureMarketsLoaded } = useInstanceMarket();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);
  const [tabValue, setTabValue] = useState<number>(0);
  const isDemoMode = isDemoModeEnabled();
  const { referrals, markReferralOnboarded } = useDemoScenario();
  const [quickOnboard, setQuickOnboard] = useState<boolean>(false);

  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  const referralId = searchParams.get('referralId');
  const activeReferral = !isEdit ? referrals.find((referral) => referral.id === referralId && referral.status === 'PendingProcurementReview') : undefined;
  const pendingReferrals = !isEdit ? referrals.filter((referral) => referral.status === 'PendingProcurementReview' && referral.id !== referralId) : [];
  
  const [formData, setFormData] = useState<MarketSupplier>({
    supplierId: 0,
    instanceName: '',
    companyId: 0,
    marketName: '',
    customerTypeId: 0,
    supplierNumber: '',
    supplierName: '',
    address1: '',
    address2: '',
    town: '',
    county: '',
    postcode: '',
    country: '',
    emailAddress: '',
    emailAddress2: '',
    fileFormat: '',
    publishMethod: '',
    supplierNumber2: '',
    cutOffTime: '',
    fileContents: 0,
    creditRequestCutOffTime: '',
    minimumOrderValue: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const markets = getMarkets(formData.companyId ? formData.companyId.toString() : '');

  const fileFormatOptions = [
    { value: 'CSV', label: 'CSV' },
    { value: 'XLS', label: 'XLS' },
    { value: 'XLSX', label: 'XLSX' },
    { value: 'PDF', label: 'PDF' },
  ];

  const publishMethodOptions = [
    { value: 'EMAIL', label: 'EMAIL' },
    { value: 'FTP', label: 'FTP' },
    { value: 'FTP & EMAIL', label: 'FTP & EMAIL' },
  ];

  const fileContentsOptions = [
    { value: 0, label: t('consolidatedFile') },
    { value: 1, label: t('singleFile') },
  ];

  const fetchSupplier = async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const result = await client.getMarketSupplier(id);
      if (result.success && result.data) {
        setFormData(result.data);
        if (result.data.companyId) {
          ensureMarketsLoaded(result.data.companyId.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching supplier:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchSupplier();
    }
  }, [id, isEdit]);

  // Prefill supplier name from a chef "refer to procurement" request (RecipeV2 -> Suppliers link).
  useEffect(() => {
    if (!isEdit && activeReferral) {
      setFormData((prev) => (prev.supplierName ? prev : { ...prev, supplierName: activeReferral.supplierName }));
    }
  }, [isEdit, activeReferral]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.supplierName.trim()) {
      newErrors.supplierName = t('supplierNameRequired');
    }

    if (!formData.supplierNumber.trim()) {
      newErrors.supplierNumber = t('supplierNumberRequired');
    }

    if (!formData.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    if (!formData.customerTypeId) {
      newErrors.customerTypeId = t('marketRequired');
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = t('emailRequired');
    }

    if (!formData.fileFormat) {
      newErrors.fileFormat = t('fileFormatRequired');
    }

    if (!formData.publishMethod) {
      newErrors.publishMethod = t('publishMethodRequired');
    }

    if (formData.minimumOrderValue !== undefined && formData.minimumOrderValue < 0) {
      newErrors.minimumOrderValue = t('minimumOrderValueMustBePositive');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketSupplier) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value: string | number = event.target.value;
    
    if (field === 'supplierId' || field === 'companyId' || field === 'customerTypeId' || field === 'fileContents' || field === 'minimumOrderValue') {
      value = value === '' ? 0 : parseInt(value) || 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      instanceName: value?.name || '',
      customerTypeId: 0, // Reset market when instance changes
      marketName: '',
    }));
    
    if (value?.id) {
      ensureMarketsLoaded(value.id.toString());
    }
    
    if (errors.companyId) {
      setErrors(prev => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    setFormData(prev => ({
      ...prev,
      customerTypeId: value?.customerTypeId || 0,
      marketName: value?.name || '',
    }));
    
    if (errors.customerTypeId) {
      setErrors(prev => ({
        ...prev,
        customerTypeId: '',
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateMarketSupplier(id!, formData);
        if (result.success) {
          setMessage(t('updateSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/suppliers${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      } else {
        const result = await client.createMarketSupplier(formData);
        if (result.success) {
          if (activeReferral) {
            markReferralOnboarded(activeReferral.id);
          }
          setMessage(t('createSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/suppliers${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit ? t('updateErrorMessage') : t('createErrorMessage')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/procurement/suppliers${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'SupplierUpdate' : 'SupplierCreate', 'ConfigurationAdmin']);
  const governanceProfile = quickOnboard
    ? createQuickOnboardGovernanceProfile(formData.supplierId || 0)
    : getSupplierGovernanceProfile(formData.supplierId || 0);

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{t(isEdit ? 'editSupplier' : 'createSupplier')}</PageTitle>
        <Typography>{t('loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack spacing={6}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <PageTitle>{t(isEdit ? 'editSupplier' : 'createSupplier')}</PageTitle>
            <Typography
              variant="textSM"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              {t('supplierFormSubtitle', 'Manage supplier setup, delivery rules, ordering preferences, and contact details.')}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={3}
            justifyContent="flex-end"
          >
            <Button
              variant="secondary"
              size="lg"
              onClick={handleCancel}
            >
              {t('cancel')}
            </Button>
            {userCanSave && (
              <Button
                variant="primary"
                size="lg"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? t('saving') : (isEdit ? t('update') : t('create'))}
              </Button>
            )}
          </Stack>
        </Box>

        {isDemoMode && activeReferral && (
          <MuiAlert severity="info">
            {t('referredByChef', 'Referred by chef from "{{product}}": {{reason}}', { product: activeReferral.requestedByProduct, reason: activeReferral.reason })}
          </MuiAlert>
        )}

        <Box>
          <Tabs
            value={tabValue}
            onChange={(_event, newValue) => setTabValue(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: '-1px',
              ml: 4,
              mr: 4,
              px: 2,
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label={t('overview', 'Overview')} />
            <Tab label={t('ordering', 'Ordering')} />
            <Tab label={t('contacts', 'Contacts')} />
            {isDemoMode && <Tab label={t('governance', 'Governance')} />}
          </Tabs>

          <Section style={{ mt: 0, p: 5, pb: 0 }}>
            <Box sx={{ p: { xs: 4, md: 5 } }}>
              <TabPanel value={tabValue}
                index={0}>
                <Grid container
                  spacing={4}>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('supplierName')}
                      value={formData.supplierName}
                      onChange={handleInputChange('supplierName')}
                      error={!!errors.supplierName}
                      helperText={errors.supplierName}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('supplierNumber')}
                      value={formData.supplierNumber}
                      onChange={handleInputChange('supplierNumber')}
                      error={!!errors.supplierNumber}
                      helperText={errors.supplierNumber}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('supplierNumber2')}
                      value={formData.supplierNumber2}
                      onChange={handleInputChange('supplierNumber2')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <Autocomplete
                      options={instances}
                      getOptionLabel={(option) => option.name}
                      value={instances.find(instance => instance.id === formData.companyId) || null}
                      onChange={handleInstanceChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('instance')}
                          error={!!errors.companyId}
                          helperText={errors.companyId}
                          required
                        />
                      )}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <Autocomplete
                      options={markets}
                      getOptionLabel={(option) => option.name}
                      value={markets.find(market => market.customerTypeId === formData.customerTypeId) || null}
                      onChange={handleMarketChange}
                      disabled={!formData.companyId}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('market')}
                          error={!!errors.customerTypeId}
                          helperText={errors.customerTypeId}
                          required
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue}
                index={1}>
                <Grid container
                  spacing={4}>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('fileFormat')}
                      select
                      value={formData.fileFormat}
                      onChange={handleInputChange('fileFormat')}
                      error={!!errors.fileFormat}
                      helperText={errors.fileFormat}
                      fullWidth
                      required
                    >
                      {fileFormatOptions.map((option) => (
                        <MenuItem key={option.value}
                          value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('fileContents')}
                      select
                      value={formData.fileContents !== undefined ? formData.fileContents : 0}
                      onChange={handleInputChange('fileContents')}
                      fullWidth
                    >
                      {fileContentsOptions.map((option) => (
                        <MenuItem key={option.value}
                          value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('publishMethod')}
                      select
                      value={formData.publishMethod}
                      onChange={handleInputChange('publishMethod')}
                      error={!!errors.publishMethod}
                      helperText={errors.publishMethod}
                      fullWidth
                      required
                    >
                      {publishMethodOptions.map((option) => (
                        <MenuItem key={option.value}
                          value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('cutOffTime')}
                      type="time"
                      value={formData.cutOffTime || ''}
                      onChange={handleInputChange('cutOffTime')}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('creditRequestCutOffTime')}
                      type="time"
                      value={formData.creditRequestCutOffTime || ''}
                      onChange={handleInputChange('creditRequestCutOffTime')}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('minimumOrderValue')}
                      type="number"
                      value={formData.minimumOrderValue || ''}
                      onChange={handleInputChange('minimumOrderValue')}
                      error={!!errors.minimumOrderValue}
                      helperText={errors.minimumOrderValue}
                      fullWidth
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue}
                index={2}>
                <Grid container
                  spacing={4}>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('emailAddress')}
                      type="email"
                      value={formData.emailAddress}
                      onChange={handleInputChange('emailAddress')}
                      error={!!errors.emailAddress}
                      helperText={errors.emailAddress}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('emailAddress2')}
                      type="email"
                      value={formData.emailAddress2}
                      onChange={handleInputChange('emailAddress2')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('address1')}
                      value={formData.address1}
                      onChange={handleInputChange('address1')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('address2')}
                      value={formData.address2}
                      onChange={handleInputChange('address2')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('town')}
                      value={formData.town}
                      onChange={handleInputChange('town')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('county')}
                      value={formData.county}
                      onChange={handleInputChange('county')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('postcode')}
                      value={formData.postcode}
                      onChange={handleInputChange('postcode')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('country')}
                      value={formData.country}
                      onChange={handleInputChange('country')}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              {isDemoMode && (
                <TabPanel value={tabValue}
                  index={3}>
                  <GovernanceTab
                    profile={governanceProfile}
                    quickOnboard={quickOnboard}
                    onQuickOnboardChange={setQuickOnboard}
                    quickOnboardDisabled={isEdit}
                  />
                </TabPanel>
              )}
            </Box>
          </Section>
        </Box>

      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default SupplierForm;
